import { observable, action, computed, reaction } from 'mobx';
import objectPath from 'object-path';
import Alert from 'react-s-alert';

import { notificationCenter } from 'apps/notificationCenter/appManager';
import { frameStore } from 'apps/components/Frame/appManager';
import { analytics } from 'core/analytics';
import { fromLocalStorage, toLocalStorage } from 'core/localStorage';
import {
  disconnectWebsocket,
  initHMACWebsocket,
  initWebsocket,
} from 'core/client';
import { routerStore } from 'core/routing';
import {
  startJwtRefreshing,
  stopJwtRefreshing,
  RemoveJwt,
  tokenData,
  refresher,
} from 'core/jwt';
import { AppRegistry, ModelRegistry } from 'core/registry';
import { AppManager, client } from 'core';

import { SpaceModel } from 'apps/space/app';
import { isMobile } from 'styles/utils';

import i18next from 'i18next';
import { i18n } from 'config/i18n';

import { maxWidths } from 'legacyComponents/styled/styleUtils';

import { WHOAMI } from 'apps/signIn/gql';

import { Uploader } from 'apps/client/uploader';

import Cookie from 'js-cookie';
import { WorkspacesQuery } from 'apps/space/gql';

import {
  communityRoutes,
  conditionalCommunityRoutes,
} from 'apps/components/Frame/config';

export class ClientStore extends AppManager {
  routerStore = routerStore;
  @observable posting = false;
  @observable context = observable.map({}, { deep: false });
  @observable footerHidden = false;
  @observable flash = null;
  @observable pendingInvitations = [];
  @observable hasBanner = false;
  @observable hasCovidNav = true;
  @observable isStaff = false;
  @observable flags = {};
  @observable isMobile = isMobile();

  @observable workspaces = [];
  @observable activeSpaceId = null;
  @observable areWorkspacesLoaded = false;

  @observable activeSpaceChangedCount = 0;

  @observable HMAC = null;

  routesReactiveToActiveSpace = [
    'workspace',
    'workspace.spaceFiles',
    'workspace.spaceProjects',
    'workspace.spaceSettings.teams',
    'workspace.spaceSettings.account',
    'workspace.spaceSettings.notifications',
    'workspace.spaceSettings.billing',
    'workspace.rfqs',
    'workspace.orders',
    'workspace.intermediator.rfqs',
    'workspace.intermediator.orders',
    'workspace.manufacturer.rfqs',
    'workspace.manufacturer.orders',
  ];

  routesWithInherentSpace = [
    'profile',
    'initiative.project',
    'initiative.project.files',
    'initiative.project.issues',
    'initiative.project.settings.teams',
    'initiative.project.settings.general',
    'profile.project',
    'profile.project.files',
    'profile.project.issues',
    'profile.project.settings.teams',
    'profile.project.settings.general',
    'initiative',
    'initiative.projects',
    'initiative.stories',
    'initiative.shared.file',
  ];

  publicBaseRoutes = ['profile', 'initiative', 'home', 'channel', 'make-part'];

  @action initialize = () => {
    this.tokenData = tokenData;
    const ProfileApp = AppRegistry.get('ProfileApp');
    const ProfileModel = ModelRegistry.get('ProfileModel');

    // NOTE (david): AppRef should be set in the constructor, otherwise reactions may to not occur if an
    // observer accesses "signedInProfile" before it's been setup with AppRef. In that case even when the
    // content is initialized the observer will not react because there was no observable in the first place.
    // Just declaring @observable signedInProfile = null; in this module does not fix this.
    // This is not in the constructor due to an issue with circular import due this being a global singleton
    // For now we hopefully call initialize() early enough that this is initialized before any
    // access to that variable from an observer happens.
    this.AppRef('signedInProfile', ProfileApp);
    this.SetSignedInProfile(
      ProfileModel.Load(fromLocalStorage('signedInProfile')),
      { toLS: false },
    );
    this.WhoAmI();
    this.LoadHMAC();
    this.uploader = new Uploader();
    this.readFlashMessage();
    this.initReactionsForSpaceAwareRoutes();
  };

  @action initReactionsForSpaceAwareRoutes() {
    // Reaction 1: If the route changes, check whether we have to move to the active space
    // set by the route.
    this.disposers.push(
      reaction(
        () => ({
          name: routerStore.route.name,
          params: routerStore.route.params,
        }),
        () => {
          this.MaybeAssignActiveWorkspaceFromRoute();
        },
      ),
    );

    // Reaction 2: If the active space changes, check whether we have to change the route displayed.
    this.disposers.push(
      reaction(
        () => this.activeSpace,
        activeSpace => {
          if (!activeSpace) return;
          const { name, params } = routerStore.route;
          if (this.routesReactiveToActiveSpace.includes(name)) {
            const paramSpacePath = params?.spacePath;
            if (paramSpacePath !== activeSpace.path) {
              routerStore.router.navigate(name, {
                ...params,
                spacePath: activeSpace.path,
              });
            }
          }
        },
      ),
    );

    // Reaction 3: Redirect home to workspace (dahsboard) route
    this.disposers.push(
      reaction(
        () => ({
          signedInProfile: this.signedInProfile,
          routeName: routerStore.route.name,
        }),
        ({ signedInProfile, routeName }) => {
          if (!signedInProfile) return;
          if (routeName === 'home') {
            let spacePath = this.activeSpace?.path;
            if (!spacePath) spacePath = signedInProfile.username;
            routerStore.navigate(
              'workspace',
              {
                spacePath,
              },
              { replace: true },
            );
          }
        },
        { fireImmediately: true },
      ),
    );
  }

  @action MaybeAssignActiveWorkspaceFromRoute() {
    const { name, params } = routerStore.route;

    if (this.routesReactiveToActiveSpace.includes(name)) {
      const paramSpacePath = params?.spacePath;
      if (paramSpacePath) {
        const routeSpace = this.workspaces.find(
          workspace => workspace?.path === paramSpacePath,
        );
        if (routeSpace) {
          this.SetActiveWorkspace(routeSpace.id);
        }
      }
    } else if (this.routesWithInherentSpace.includes(name)) {
      const paramSpacePath = params?.profile || params?.initiative;
      if (paramSpacePath) {
        const routeSpace = this.workspaces.find(
          workspace => workspace?.path === paramSpacePath,
        );
        if (routeSpace) {
          this.SetActiveWorkspace(routeSpace.id);
        }
      }
    }
  }

  @action MaybeNavigateToTheDefaultSpaceRoute() {
    const routeName = routerStore.route.name;
    if (!this.routesReactiveToActiveSpace.includes(routeName)) {
      routerStore.navigate('home');
    }
  }

  disposeWhen() {
    // never dispose this manager
    return false;
  }

  next() {
    const nextParam = this.routerStore.params.get('next');
    if (nextParam) {
      return nextParam;
    }
    return window.location.pathname + window.location.search;
  }

  @action readFlashMessage = () => {
    const flashCookie = Cookie.getJSON('flash_messages');
    if (flashCookie) {
      const decodedCookie = atob(flashCookie);
      try {
        const cookieObject = JSON.parse(decodedCookie);
        this.flash = cookieObject;
      } finally {
        Cookie.set('flash_messages', '', { expires: 0 });
      }
    }
  };

  @action SetupPollQueries = () => {
    if (this.signedInProfile && !notificationCenter.polling) {
      notificationCenter.NewNotificationsPollQuery();
    } else if (!this.signedInProfile && notificationCenter.polling) {
      notificationCenter.StopNotificationsPollQuery();
    }

    if (this.signedInProfile && !frameStore.inboxManager?.isPolling) {
      frameStore.inboxManager?.NewInboxPollQuery();
    } else if (!this.signedInProfile && frameStore.inboxManager?.isPolling) {
      frameStore.inboxManager?.StopInboxPollQuery();
    }
  };

  @action ResetFlash = () => {
    this.flash = null;
  };

  @action SetPendingInvitations = pendingInvitations => {
    this.pendingInvitations = pendingInvitations;
  };

  @action RemoveInvitation = invitation => {
    this.pendingInvitations = this.pendingInvitations.filter(
      ({ id }) => id !== invitation.id,
    );
  };

  @action SetSignedInProfile = (profile, options = { toLS: true }) => {
    const { toLS } = options;
    this.signedInProfile = profile;
    if (profile) {
      startJwtRefreshing();
      analytics.linkUser(profile);
      i18n.changeLanguage(profile.locale);
      initWebsocket();
    } else {
      analytics.unlinkUser();
      this.SetIsStaff(false);
    }
    if (toLS) {
      toLocalStorage('signedInProfile', profile);
    }
    this.SetupPollQueries();
  };

  @action onSignOutErr = () => {
    Alert.error(
      i18next.t(
        'Sorry, we were unable to sign you out. Please check your network connection and try again.',
      ),
    );
  };

  @action SignOutUser = () => {
    this.context.get('signInAppManager').SignOut({
      onSysErr: this.CompleteSignOut,
      onUserErr: this.CompleteSignOut,
      onSuccess: this.CompleteSignOut,
    });
    // RemoveJwt();
  };

  @action CompleteSignOut = () => {
    routerStore.navigate('home');

    client.stop();

    this.SetSignedInProfile(null);

    disconnectWebsocket();
    // remove the jwt and stop the refreshing
    stopJwtRefreshing();
    RemoveJwt();

    this.areWorkspacesLoaded = false;
    this.workspaces = [];

    if (
      this.routerStore.route.name !== 'home.signIn' &&
      this.routerStore.route.name !== 'home.signUp'
    ) {
      window.location.reload();
    }

    client.clearStore();
  };

  async WhoAmI() {
    const params = { query: WHOAMI, fetchPolicy: 'no-cache' };
    try {
      const { data } = await client.query(params);
      const ProfileModel = ModelRegistry.get('ProfileModel');

      const profileData = objectPath.get(data, 'loggedInUser.profile', null);
      const isStaff = objectPath.get(data, 'loggedInUser.isStaff', false);
      const flagsJSON = objectPath.get(data, 'loggedInUser.flagsJSON', false);

      const profile = ProfileModel.Load(profileData);

      this.SetSignedInProfile(profile);
      this.SetIsStaff(isStaff);
      const flags = JSON.parse(flagsJSON);
      this.SetFlags(flags);
      if (profile)
        this.QueryAvailableSpaces(this.activeSpaceChangedCount === 0);
      else {
        this.workspaces = [];
        this.SetActiveWorkspace(null);
      }
      return profile;
    } catch (err) {
      analytics.logError(err);
    }

    return null;
  }

  // Role and header management

  hasRole = roleName => {
    if (roleName === 'USER') {
      return true; // check for logged in?
    }
    if (roleName === 'INTERMEDIATOR') {
      if (tokenData.jwtData?.role === 'INTERMEDIATOR') {
        return true;
      }
      return false;
    }
    if (roleName === 'ADMIN') {
      if (tokenData.jwtData?.role === 'ADMIN') {
        return true;
      }
      return false;
    }
    return false;
  };

  @computed get currentPortal() {
    if (!tokenData.jwtData?.role) {
      return '';
    }
    if (
      (this.hasRole('INTERMEDIATOR') &&
        routerStore.route.name.startsWith('intermediator')) ||
      routerStore.route.name.startsWith('workspace.intermediator')
    ) {
      return 'INTERMEDIATOR';
    }
    return 'USER';
  }

  @action ToggleCurrentPortal = () => {
    const routeName = routerStore.route.name;
    // remove filters when switching portals as we may have different filters available
    const newParams = Object.assign({}, routerStore.route.params);
    delete newParams.filters;

    if (this.currentPortal === 'USER' && this.hasRole('INTERMEDIATOR')) {
      if (routeName.startsWith('workspace')) {
        routerStore.router.navigate(
          routeName.replace('workspace', 'workspace.intermediator'),
          newParams,
        );
      } else {
        routerStore.router.navigate(`intermediator.${routeName}`, newParams);
      }
    } else if (this.currentPortal === 'INTERMEDIATOR') {
      routerStore.router.navigate(
        routeName
          .replace('workspace.intermediator', 'workspace')
          .replace('intermediator.', ''),
        newParams,
      );
    }
  };

  @computed get roleHeaders() {
    if (this.currentPortal === 'USER') {
      return { 'X-Wikifactory-Role': 'USER' };
    }
    return {};
  }

  @computed get routeSegments() {
    return routerStore.route.name.split('.');
  }

  // Space utils
  @computed get spaceRoute() {
    const route = routerStore.route.name.split('.');
    let sRoute = route[0];
    if (!['profile', 'initiative', 'channel', 'project'].includes(sRoute)) {
      // return {route:'home', exact: (sRoute === 'home')};
      return { route: 'home', exact: route.length === 1 };
    }
    if (route[1] === 'project' || route[1] === 'collection') {
      sRoute = route.slice(0, 2).join('.');
    }
    return {
      route: sRoute,
      exact: sRoute === routerStore.route.name,
      type: sRoute.split('.')[-1],
    };
  }

  @computed get spaceParams() {
    const spaceRoute = this.spaceRoute.route;
    if (['profile', 'initiative'].includes(spaceRoute)) {
      return { [`${spaceRoute}`]: routerStore.params.get(spaceRoute) };
    }
    if (spaceRoute === 'channel') {
      return { channel: routerStore.params.get('channel') };
    }
    if (spaceRoute === 'profile.project') {
      return {
        profile: routerStore.params.get('profile'),
        slug: routerStore.params.get('slug'),
      };
    }
    if (spaceRoute === 'initiative.project') {
      return {
        initiative: routerStore.params.get('initiative'),
        slug: routerStore.params.get('slug'),
      };
    }
    return {};
  }

  @computed get spacePath() {
    return routerStore.router
      .buildPath(this.spaceRoute.route, this.spaceParams)
      .slice(1);
  }

  @computed get atCommunityRoute() {
    if (communityRoutes.includes(routerStore.route.name)) return true;

    const routeSlugPath =
      routerStore.route.params.profile || routerStore.route.params.initiative;
    if (
      conditionalCommunityRoutes.includes(routerStore.route.name) &&
      !this.getWorkspaceWithSlugPath(routeSlugPath)
    ) {
      return true;
    }
    return false;
  }

  @observable locale = 'en';
  @action SetPosting = bool => {
    this.posting = bool;
  };

  @action SetFooterHidden = bool => {
    this.footerHidden = bool;
  };

  @observable scrollTop = 0;
  @observable checkingScroll = false;
  @action ScrollToTop = () => {
    window.requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
  };

  @computed get headerIsFixed() {
    if (this.routerStore.route.name.includes('home.discover')) {
      return false;
    }
    const fixedHeaderRoutes = [
      'profile',
      'profile.project',
      'profile.collection',
      'initiative',
      'initiative.project',
      'initiative.collection',
    ];
    if (
      fixedHeaderRoutes.includes(this.spaceRoute.route) &&
      this.spaceRoute.exact
    ) {
      return false;
    }
    return true;
  }

  @observable windowWidth = window.innerWidth;
  @observable windowHeight = window.innerHeight;
  @computed get activeBreakPoint() {
    if (this.windowWidth > maxWidths.l) {
      return 'default';
    }
    if (this.windowWidth <= maxWidths.l && this.windowWidth > maxWidths.m) {
      return 'l';
    }
    if (this.windowWidth <= maxWidths.m && this.windowWidth > maxWidths.s) {
      return 'm';
    }
    if (this.windowWidth <= maxWidths.s && this.windowWidth > maxWidths.xs) {
      return 's';
    }
    if (this.windowWidth <= maxWidths.xs) {
      return 'xs';
    }
    return 'default';
  }

  @action setLocale(newLocale) {
    this.locale = newLocale;
  }

  @action setCheckingScroll(bool) {
    this.checkingScroll = bool;
  }

  @action setScrollTop(top) {
    this.scrollTop = top;
  }

  @action setWindowSize(width, height) {
    this.windowWidth = width;
    this.windowHeight = height;
  }

  @action SetHasBanner = bool => {
    this.hasBanner = bool;
    if (!bool) this.hasCovidNav = true;
  };

  @action SetIsStaff(staff) {
    this.isStaff = !!staff;
  }

  @action SetFlags(flags) {
    this.flags = flags;
  }

  @action QueryAvailableSpaces(
    shouldTryToSetActiveWorkspaceFromRoute = false,
    onSuccess,
  ) {
    if (this.hasRole('ADMIN')) {
      return;
    }
    const query = new WorkspacesQuery(this, {
      variables: {
        type: 'Space',
      },
    });
    query.process(
      response => {
        if (!response) return null;
        const workspaces = [];
        response.forEach(data => {
          workspaces.push(SpaceModel.Load(data));
        });
        return workspaces;
      },
      spaces => {
        if (!spaces) return;
        if (/*! spaces || */ spaces.length === 0) {
          // only set flag to loaded if we got empty data

          // if (spaces && spaces.length === 0)
          this.areWorkspacesLoaded = true;
          this.SetActiveWorkspace(null);
          return;
        }
        this.workspaces = spaces
          .filter(space => space.spaceType !== 'GLOBAL')
          .sort((a, b) => {
            if (a.spaceType === b.spaceType) return 0;
            if (a.spaceType === 'PROFILE') return -1; // a first
            return 1;
          });
        this.areWorkspacesLoaded = true;

        if (shouldTryToSetActiveWorkspaceFromRoute)
          this.MaybeAssignActiveWorkspaceFromRoute();
        if (!this.activeSpaceId && this.workspaces.length > 0) {
          this.SetActiveWorkspace(this.workspaces[0]?.id);
        }
        onSuccess?.();
      },
    );
    query.query();
  }

  // format: slug === 'user001'
  getWorkspaceWithSlug = slug => {
    if (!slug) return null;
    let sanitizedSlug = slug;
    // Ideally this should not be necessary, but it is because some slugs from server return as [@+]slug
    if (slug[0] === '+' || slug[0] === '@') {
      sanitizedSlug = slug.substring(1);
    }
    const matchingSpace = this.workspaces.find(
      workspace => workspace.content.slug === sanitizedSlug,
      // || workspace.content.slug === slug,
    );
    return matchingSpace;
  };

  // format: slugPath === '@user001'
  getWorkspaceWithSlugPath = slugPath => {
    if (!slugPath) return null;
    let slug = null;
    if (slugPath[0] === '+' || slugPath[0] === '@') {
      slug = slugPath.substring(1);
    }
    return this.getWorkspaceWithSlug(slug);
  };

  @action SetActiveWorkspaceWithSlugIfValid(slug) {
    return this.SetActiveWorkspaceIfValid(this.getWorkspaceWithSlug(slug)?.id);
  }

  @action SetActiveWorkspaceIfValid(spaceId) {
    if (!spaceId) return false;
    if (this.workspaces.some(workspace => workspace.id === spaceId)) {
      this.SetActiveWorkspace(spaceId);
      return true;
    }
    return false;
  }

  @action SetActiveWorkspace(spaceId) {
    if (spaceId && this.activeSpaceId !== spaceId)
      this.activeSpaceChangedCount += 1;
    this.activeSpaceId = spaceId;
  }

  @computed get activeSpace() {
    return this.workspaces.find(space => space.id === this.activeSpaceId);
  }

  @computed get isTheCurrentActiveSpaceTheFirstOneUsed() {
    return this.activeSpaceChangedCount === 1;
  }

  @action SetIsMobile(_isMobile) {
    this.isMobile = _isMobile;
  }

  @action RequestHMAC = () => {
    fetch(`${API_URL_BASE}/hmac`, {})
      .then(response => {
        if (!response.ok) {
          throw Error(response.statusText);
        }
        return response.text();
      })
      .then(this.SetHMAC);
  };

  @action SetHMAC = newHMAC => {
    this.HMAC = newHMAC;
    initHMACWebsocket(this.HMAC);
    sessionStorage.setItem('HMAC', this.HMAC);
  };

  @action LoadHMAC = () => {
    const storageHMAC = sessionStorage.getItem('HMAC');
    if (!storageHMAC) return;
    this.SetHMAC(storageHMAC);
  };
}

export const clientStore = new ClientStore();
refresher.clientStore = clientStore;
